import { readFile } from 'node:fs/promises'
import { Command } from 'commander'
import { createApiClient } from '../http.js'
import { printJson } from '../output.js'

/**
 * `verentis api` — authenticated escape hatch for any gateway endpoint (like `gh api`).
 * Gives full API parity for operations without a dedicated command yet.
 */
export function registerApi(program: Command): void {
  program
    .command('api <method> <path>')
    .description('Call any platform API endpoint with automatic authentication (e.g. `verentis api GET /v1/workspaces`)')
    .option('--scope <scope...>', 'scopes to request on the access token (repeatable)')
    .option('--workspace <workspaceId>', 'bind the access token to a workspace')
    .option('--body <json>', 'JSON request body (use @file.json to read from a file, or - for stdin)')
    .option('--query <key=value...>', 'query string parameters (repeatable)')
    .option('--content-type <type>', 'content type for --body when not JSON')
    .option('--raw', 'print the raw response body without JSON pretty-printing')
    .action(async (method: string, path: string, options: { scope?: string[]; workspace?: string; body?: string; query?: string[]; contentType?: string; raw?: boolean }) => {
      const api = await createApiClient()
      const query: Record<string, string> = {}
      for (const pair of options.query ?? []) {
        const idx = pair.indexOf('=')
        if (idx < 0) throw new Error(`Invalid --query '${pair}' (expected key=value).`)
        query[pair.slice(0, idx)] = pair.slice(idx + 1)
      }

      let body: unknown
      let rawBody: BodyInit | undefined
      if (options.body !== undefined) {
        let text = options.body
        if (text === '-') text = await readStdin()
        else if (text.startsWith('@')) text = await readFile(text.slice(1), 'utf8')
        if (options.contentType && !options.contentType.includes('json')) rawBody = text
        else body = JSON.parse(text)
      }

      const res = await api.requestRaw(method.toUpperCase(), path, {
        scopes: options.scope ?? [],
        tokenWorkspaceId: options.workspace,
        body,
        rawBody,
        contentType: options.contentType,
        query,
      })

      const text = await res.text()
      if (!text) {
        console.error(`${res.status} ${res.statusText}`)
        return
      }
      if (options.raw) {
        process.stdout.write(text)
        return
      }
      try {
        printJson(JSON.parse(text))
      } catch {
        process.stdout.write(text)
      }
    })
}

async function readStdin(): Promise<string> {
  const chunks: Buffer[] = []
  for await (const chunk of process.stdin) chunks.push(chunk as Buffer)
  return Buffer.concat(chunks).toString('utf8')
}
